// ============================================================
// Failure Chain Editor Component
// ============================================================
// AIAG-VDA Step 4: Create and edit failure chains
// Part of the "Golden Thread" data flow
// ============================================================

import React, { useState } from 'react';
import { useAnalysis } from '../contexts/AnalysisContext';
import { useAppContext } from '../contexts/AppContext';
import { useTheme } from '../contexts/ThemeContext';
import {
    Plus,
    Trash2,
    ChevronDown,
    ChevronRight,
    AlertTriangle,
    ArrowRight,
    Link2,
    Save,
    X,
} from 'lucide-react';
import clsx from 'clsx';
import type { FailureChain } from '../services/analysisService';
import type { CauseCategory, ProcessStep, ProcessFunction } from '../types';

// ============================================================
// SUB-COMPONENTS (Low Complexity)
// ============================================================

interface CauseCategorySelectProps {
    value: CauseCategory;
    onChange: (value: CauseCategory) => void;
    isDark: boolean;
}

const CauseCategorySelect: React.FC<CauseCategorySelectProps> = ({ value, onChange, isDark }) => {
    const categories: { value: CauseCategory; label: string; color: string }[] = [
        { value: 'Man', label: 'Man (Operator)', color: 'bg-blue-500' },
        { value: 'Machine', label: 'Machine (Equipment)', color: 'bg-purple-500' },
        { value: 'Material', label: 'Material (Input)', color: 'bg-orange-500' },
        { value: 'Method', label: 'Method (Process)', color: 'bg-green-500' },
        { value: 'Measurement', label: 'Measurement', color: 'bg-cyan-500' },
        { value: 'Environment', label: 'Environment', color: 'bg-teal-500' },
    ];

    return (
        <select
            value={value}
            onChange={e => onChange(e.target.value as CauseCategory)}
            className={clsx(
                'px-3 py-2 rounded border text-sm',
                isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'
            )}
        >
            {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
        </select>
    );
};

interface FailureChainCardProps {
    chain: FailureChain;
    step?: ProcessStep;
    func?: ProcessFunction;
    onUpdate: (updates: Partial<FailureChain>) => void;
    onDelete: () => void;
    isDark: boolean;
    isExpanded: boolean;
    onToggle: () => void;
}

const FailureChainCard: React.FC<FailureChainCardProps> = ({
    chain,
    step,
    func,
    onUpdate,
    onDelete,
    isDark,
    isExpanded,
    onToggle,
}) => {
    const isComplete = chain.failureMode && chain.failureEffectEnd && chain.failureCause;

    return (
        <div className={clsx(
            'rounded-lg border transition-all',
            isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200',
            isComplete ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-amber-500'
        )}>
            {/* Header */}
            <button
                onClick={onToggle}
                className={clsx(
                    'w-full flex items-center justify-between p-3 text-left',
                    isDark ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'
                )}
            >
                <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    <div>
                        <div className={clsx('font-medium', isDark ? 'text-white' : 'text-slate-800')}>
                            {chain.failureMode || 'New Failure Chain'}
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-2">
                            <span>{step?.stepNumber} {step?.stepName}</span>
                            {func && (
                                <>
                                    <ArrowRight size={10} />
                                    <span className="truncate max-w-[200px]">{func.functionDescription}</span>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className={clsx(
                        'text-xs px-2 py-0.5 rounded',
                        chain.status === 'linked'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                            : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                    )}>
                        {chain.status}
                    </span>
                    <button
                        onClick={e => { e.stopPropagation(); onDelete(); }}
                        className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                    >
                        <Trash2 size={14} />
                    </button>
                </div>
            </button>

            {/* Content */}
            {isExpanded && (
                <div className={clsx(
                    'p-4 border-t space-y-4',
                    isDark ? 'border-slate-700' : 'border-slate-200'
                )}>
                    {/* 4M Category */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">
                            4M Category (Cause Type)
                        </label>
                        <CauseCategorySelect
                            value={chain.causeCategory}
                            onChange={causeCategory => onUpdate({ causeCategory })}
                            isDark={isDark}
                        />
                    </div>

                    {/* Failure Mode */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">
                            Failure Mode (Anti-Function) *
                        </label>
                        <input
                            type="text"
                            value={chain.failureMode}
                            onChange={e => onUpdate({ failureMode: e.target.value })}
                            placeholder="e.g., Weak weld penetration"
                            className={clsx(
                                'w-full px-3 py-2 rounded border text-sm',
                                isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'
                            )}
                        />
                    </div>

                    {/* Failure Cause */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">
                            Failure Cause *
                        </label>
                        <input
                            type="text"
                            value={chain.failureCause}
                            onChange={e => onUpdate({ failureCause: e.target.value })}
                            placeholder="e.g., Insufficient welding current"
                            className={clsx(
                                'w-full px-3 py-2 rounded border text-sm',
                                isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'
                            )}
                        />
                    </div>

                    {/* Effects Chain */}
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">
                                Local Effect
                            </label>
                            <textarea
                                value={chain.failureEffectLocal || ''}
                                onChange={e => onUpdate({ failureEffectLocal: e.target.value })}
                                placeholder="Effect at this step"
                                rows={2}
                                className={clsx(
                                    'w-full px-3 py-2 rounded border text-sm resize-none',
                                    isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'
                                )}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">
                                Next-Level Effect
                            </label>
                            <textarea
                                value={chain.failureEffectNext || ''}
                                onChange={e => onUpdate({ failureEffectNext: e.target.value })}
                                placeholder="Effect on next step"
                                rows={2}
                                className={clsx(
                                    'w-full px-3 py-2 rounded border text-sm resize-none',
                                    isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'
                                )}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">
                                End Effect (Customer) *
                            </label>
                            <textarea
                                value={chain.failureEffectEnd}
                                onChange={e => onUpdate({ failureEffectEnd: e.target.value })}
                                placeholder="Impact on customer"
                                rows={2}
                                className={clsx(
                                    'w-full px-3 py-2 rounded border text-sm resize-none',
                                    isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'
                                )}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

interface FailureChainEditorProps {
    onClose?: () => void;
}

export const FailureChainEditor: React.FC<FailureChainEditorProps> = ({ onClose }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    // Note: activeProjectName is required by useAnalysis context internally
    useAppContext();

    const {
        processSteps,
        processFunctions,
        failureChains,
        addFailureChain,
        updateFailureChain,
        deleteFailureChain,
        saveAnalysis,
        isSyncing,
    } = useAnalysis();

    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [selectedStepId, setSelectedStepId] = useState<string>('');
    const [selectedFuncId, setSelectedFuncId] = useState<string>('');

    // Get functions for selected step
    const stepFunctions = processFunctions.filter(f => f.processStepId === selectedStepId);

    // Handle adding new chain
    const handleAddChain = () => {
        if (!selectedStepId) {
            alert('Please select a process step first.');
            return;
        }

        // Use selected function or first available
        const funcId = selectedFuncId || (stepFunctions[0]?.id || '');
        if (!funcId && stepFunctions.length === 0) {
            alert('No functions defined for this step. Go to Function Matrix to link requirements first.');
            return;
        }

        const chain = addFailureChain(selectedStepId, funcId, 'Method');
        setExpandedId(chain.id);
    };

    // Build lookup maps
    const stepMap = new Map(processSteps.map(s => [s.id, s]));
    const funcMap = new Map(processFunctions.map(f => [f.id, f]));

    // Count complete chains
    const completeCount = failureChains.filter(c => c.failureMode && c.failureEffectEnd && c.failureCause).length;

    return (
        <div className={clsx(
            'h-full flex flex-col',
            isDark ? 'bg-slate-900' : 'bg-slate-50'
        )}>
            {/* Header */}
            <div className={clsx(
                'flex items-center justify-between p-4 border-b',
                isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
            )}>
                <div className="flex items-center gap-3">
                    <AlertTriangle size={24} className="text-amber-500" />
                    <div>
                        <h2 className={clsx('text-lg font-bold', isDark ? 'text-white' : 'text-slate-800')}>
                            Failure Chain Editor
                        </h2>
                        <p className="text-xs text-slate-500">
                            AIAG-VDA Step 4: Define Cause → Mode → Effect chains
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">
                        {completeCount} / {failureChains.length} complete
                    </span>
                    <button
                        onClick={saveAnalysis}
                        disabled={isSyncing}
                        className="flex items-center gap-2 px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-white rounded text-sm"
                    >
                        <Save size={16} />
                        Save
                    </button>
                    {onClose && (
                        <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded">
                            <X size={18} />
                        </button>
                    )}
                </div>
            </div>

            {/* Add Chain Section */}
            <div className={clsx(
                'p-4 border-b flex items-end gap-3 flex-wrap',
                isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-100 border-slate-200'
            )}>
                <div className="flex-1 min-w-[200px]">
                    <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">
                        Process Step
                    </label>
                    <select
                        value={selectedStepId}
                        onChange={e => { setSelectedStepId(e.target.value); setSelectedFuncId(''); }}
                        className={clsx(
                            'w-full px-3 py-2 rounded border text-sm',
                            isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'
                        )}
                    >
                        <option value="">Select a step...</option>
                        {processSteps.map(step => (
                            <option key={step.id} value={step.id}>
                                {step.stepNumber} - {step.stepName}
                            </option>
                        ))}
                    </select>
                </div>

                {selectedStepId && stepFunctions.length > 0 && (
                    <div className="flex-1 min-w-[200px]">
                        <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">
                            Function (Optional)
                        </label>
                        <select
                            value={selectedFuncId}
                            onChange={e => setSelectedFuncId(e.target.value)}
                            className={clsx(
                                'w-full px-3 py-2 rounded border text-sm',
                                isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'
                            )}
                        >
                            <option value="">Any function</option>
                            {stepFunctions.map(func => (
                                <option key={func.id} value={func.id}>
                                    {func.functionDescription}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                <button
                    onClick={handleAddChain}
                    disabled={!selectedStepId}
                    className={clsx(
                        'flex items-center gap-2 px-4 py-2 rounded text-sm font-medium',
                        selectedStepId
                            ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                            : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                    )}
                >
                    <Plus size={16} />
                    Add Failure Chain
                </button>
            </div>

            {/* Chains List */}
            <div className="flex-1 overflow-auto p-4 space-y-3">
                {failureChains.length === 0 ? (
                    <div className="text-center py-12">
                        <Link2 size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-4" />
                        <h3 className={clsx('text-lg font-medium mb-2', isDark ? 'text-white' : 'text-slate-800')}>
                            No Failure Chains Defined
                        </h3>
                        <p className="text-slate-500 mb-4">
                            Select a process step above and click "Add Failure Chain" to start defining potential failures.
                        </p>
                    </div>
                ) : (
                    failureChains.map(chain => (
                        <FailureChainCard
                            key={chain.id}
                            chain={chain}
                            step={stepMap.get(chain.processStepId)}
                            func={funcMap.get(chain.processFunctionId)}
                            onUpdate={updates => updateFailureChain(chain.id, updates)}
                            onDelete={() => deleteFailureChain(chain.id)}
                            isDark={isDark}
                            isExpanded={expandedId === chain.id}
                            onToggle={() => setExpandedId(expandedId === chain.id ? null : chain.id)}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

export default FailureChainEditor;
